import React, { createContext, useState, useEffect } from "react";
import { setTokens, getAccessToken, getRefreshToken, clearTokens, setApiToken, getApiToken, initTokenStorage } from "../utils/tokenStorage";
import { fetchCurrentUser } from "../api/services/currentUserService";
import { getApiBaseUrl } from "../api/config";
import { tryDeviceAuth } from "../api/services/deviceService";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessTokenState] = useState(null);
  const [refreshToken, setRefreshTokenState] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Lade User-Informationen
  const loadUserInfo = async () => {
    try {
      console.log("AuthContext: Loading user info...");
      const user = await fetchCurrentUser();
      console.log("AuthContext: User info received:", JSON.stringify(user));
      setCurrentUser(user);
    } catch (error) {
      console.error("AuthContext: Failed to load user info:", error);
      console.error("AuthContext: Error type:", typeof error);
      console.error("AuthContext: Error details:", error.response?.data);
      console.error("AuthContext: Error status:", error.response?.status);
      // Bei Fehler: Logout durchführen
      logout();
      throw error; // Re-throw so login page can catch it
    }
  };

  // Session-Persistenz: Lade Tokens beim Start
  useEffect(() => {
    const initializeAuth = async () => {
      console.log("AuthContext: Initializing authentication...");

      try {
        // Initialisiere Token-Storage (lädt aus Capacitor Preferences)
        await initTokenStorage();

        // Load API URL from preferences first
        await getApiBaseUrl();

        const storedAccessToken = getAccessToken();
        const storedRefreshToken = getRefreshToken();

        // Prüfe zuerst API-Token (QR-Code Login)
        const storedApiToken = getApiToken();

        if (storedApiToken) {
          console.log("AuthContext: Found stored API token, validating...");
          setAccessTokenState(storedApiToken);

          try {
            const user = await fetchCurrentUser();
            console.log("AuthContext: API Token validation successful, user loaded");
            setCurrentUser(user);
            setIsAuthenticated(true);
          } catch (error) {
            console.error("AuthContext: API Token validation failed:", error.message);
            await clearTokens();
            setAccessTokenState(null);
            setCurrentUser(null);
            setIsAuthenticated(false);
          }
        } else if (storedAccessToken && storedRefreshToken) {
          console.log("AuthContext: Found stored JWT tokens, validating...");

          // Setze Tokens temporär
          setAccessTokenState(storedAccessToken);
          setRefreshTokenState(storedRefreshToken);

          // Lade User-Informationen zur Validierung
          try {
            const user = await fetchCurrentUser();
            console.log("AuthContext: Token validation successful, user loaded");
            setCurrentUser(user);
            setIsAuthenticated(true);
          } catch (error) {
            // Token ungültig oder API nicht erreichbar
            console.error("AuthContext: Token validation failed, clearing tokens:", error.message);
            console.log("AuthContext: User will be redirected to login");

            // Bereinige alles
            await clearTokens();
            setAccessTokenState(null);
            setRefreshTokenState(null);
            setCurrentUser(null);
            setIsAuthenticated(false);
          }
        } else {
          console.log("AuthContext: No stored tokens found, trying device-based auth...");

          // Versuche Device-basierte Authentifizierung
          try {
            const deviceAuth = await tryDeviceAuth();
            if (deviceAuth) {
              console.log("AuthContext: Device auth successful for user:", deviceAuth.username);

              // Speichere Token und setze Auth-Status
              await setApiToken(deviceAuth.token);
              setAccessTokenState(deviceAuth.token);
              setIsAuthenticated(true);

              // Lade User-Informationen
              const user = await fetchCurrentUser();
              setCurrentUser(user);
              console.log("AuthContext: Device auth complete, user loaded");
            } else {
              console.log("AuthContext: Device not registered, user needs to login");
            }
          } catch (deviceError) {
            console.log("AuthContext: Device auth failed, user needs to login:", deviceError.message);
          }
        }
      } catch (error) {
        console.error("AuthContext: Initialization error:", error);
        // Bei jedem Fehler: sauberer Zustand
        await clearTokens();
        setAccessTokenState(null);
        setRefreshTokenState(null);
        setCurrentUser(null);
        setIsAuthenticated(false);
      } finally {
        // Loading immer beenden, egal was passiert
        setIsLoading(false);
        console.log("AuthContext: Initialization complete");
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (tokens) => {
    console.log("AuthContext: login called with tokens:", JSON.stringify(tokens));
    console.log("AuthContext: tokens.access:", tokens.access);
    console.log("AuthContext: tokens.refresh:", tokens.refresh);

    if (!tokens.access || !tokens.refresh) {
      console.error("AuthContext: Missing access or refresh token!");
      throw new Error("Invalid token response from server");
    }

    await setTokens(tokens.access, tokens.refresh);
    setAccessTokenState(tokens.access);
    setRefreshTokenState(tokens.refresh);
    setIsAuthenticated(true);

    console.log("AuthContext: Tokens set, now loading user info...");
    // Lade User-Informationen nach Login
    await loadUserInfo();
    console.log("AuthContext: User info loaded successfully");
  };

  const logout = async () => {
    await clearTokens();
    setAccessTokenState(null);
    setRefreshTokenState(null);
    setCurrentUser(null);
    setIsAuthenticated(false);
  };

  // API-Token basierte Authentifizierung (für QR-Code Login)
  const setApiTokenAuth = async (token, username) => {
    console.log("AuthContext: API Token auth called for user:", username);

    // Speichere API-Token (async)
    await setApiToken(token);

    // Setze als Access-Token für API-Anfragen (apiClient verwendet getAccessToken)
    // Wir setzen den API-Token auch als "access token" damit der apiClient ihn nutzt
    // Der Unterschied: API-Token nutzt "Token xxx" Header statt "Bearer xxx"
    setAccessTokenState(token);
    setIsAuthenticated(true);

    // Lade User-Informationen
    try {
      await loadUserInfo();
      console.log("AuthContext: API Token auth successful");
    } catch (error) {
      console.error("AuthContext: API Token auth failed:", error);
      logout();
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
      accessToken,
      refreshToken,
      currentUser,
      isAuthenticated,
      isLoading,
      login,
      logout,
      setApiTokenAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};